/**
 * What an automation record can actually do — derived from the RECORD, not the
 * entity type.
 *
 * The automation slideout resolves its sections from these predicates because
 * the variation *within* `ahoy_event_automation` is wider than the variation
 * between entity types: an agent-authored automation has a script but no
 * config; lead rotation has config and state but no script; the reminders
 * automation is centrally managed and must offer no controls at all. A
 * per-type section list cannot express any of that.
 *
 * Every predicate reads only properties the record-detail payload carries.
 */

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import {
  AUTOMATION_TEMPLATES,
  INTERNAL_SCRIPT_NAMES,
  WEBSITE_SCRIPT_NAMES,
} from './templates';

/**
 * Every script name our templates can create, managed or not. Derived from the
 * template registry so a new template is automatically "built-in" here without
 * a second list to forget.
 */
const AUTOMATION_TEMPLATE_SCRIPT_NAMES: ReadonlySet<string> = new Set(
  AUTOMATION_TEMPLATES.flatMap(t => [...t.scriptNames])
);

/**
 * How the automation fires.
 *
 * - `matchAll` — fires on the named event types across EVERY entity type
 *   (`ahoy_match_all_entity_types`); the entity-type list is ignored.
 * - `typed` — fires on specific entity types × event types.
 * - `unknown` — no trigger properties present. Covers webhook-driven
 *   automations too: detecting those needs the 1:1 inbound-webhook
 *   association, which the capabilities derivation deliberately does not
 *   fetch — a pure function over one object stays cheap and synchronous.
 *   The documentation covers the webhook story in prose until a dedicated
 *   endpoint section exists (recorded as out of scope in the plan).
 */
export type AutomationTriggerKind = 'typed' | 'matchAll' | 'unknown';

export interface AutomationCapabilities {
  /**
   * The logic lives in a registered Python module, not on the record.
   * True for every script name we recognise as ours — there is no Code to
   * show and "Edit with Ahoy" has nothing to rewrite.
   */
  isBuiltIn: boolean;
  /**
   * Centrally managed (internal or website-owned): the record offers NO
   * user controls — no enable toggle, no editable settings. A managed
   * automation that renders a toggle renders a control guaranteed to fail.
   */
  isManaged: boolean;
  /** The record carries an agent-authored script worth showing. */
  hasScript: boolean;
  /** The record carries a non-empty `ahoy_script.fixtures` array to show. */
  hasFixtures: boolean;
  /** A config schema exists, so a Settings form can render. */
  hasConfig: boolean;
  /** A state schema exists, so a run-to-run State readout can render. */
  hasState: boolean;
  triggerKind: AutomationTriggerKind;
}

/** `{source, fixtures}` as stored in `ahoy_script`. */
interface ScriptValue {
  source?: unknown;
  fixtures?: unknown;
}

function isNonEmptyObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  );
}

function isNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/**
 * The record carries agent-authored test fixtures worth showing —
 * `ahoy_script.fixtures` is a non-empty array. Built-in/managed automations
 * carry no script (and so no fixtures) on the record; an empty array is not
 * fixtures. Derived from the record, never the type.
 */
export function hasFixtures(object: CrmObject): boolean {
  const script = getPropertyValue<ScriptValue>(
    object.properties?.['ahoy_script']
  );
  return isNonEmptyArray(script?.fixtures);
}

export function deriveAutomationCapabilities(
  object: CrmObject
): AutomationCapabilities {
  const properties = object.properties ?? {};

  const scriptName = getPropertyValue<string>(
    properties['ahoy_automation_name']
  );
  // Managed means "we own it, users don't": internal automations are hidden
  // behind the internal_automations flag; website automations are managed in
  // Websites settings. Both must render read-only if they reach this view.
  const isManaged =
    typeof scriptName === 'string' &&
    (INTERNAL_SCRIPT_NAMES.has(scriptName) ||
      WEBSITE_SCRIPT_NAMES.has(scriptName));
  // Built-in ⊇ managed, plus the user-owned templates (lead rotation, CRM
  // ingestion): anything whose name maps to a registered module. Agent-authored
  // automations get a generated `mapping_<hex>` name, which matches no set.
  const isBuiltIn =
    isManaged ||
    (typeof scriptName === 'string' &&
      AUTOMATION_TEMPLATE_SCRIPT_NAMES.has(scriptName));

  const script = getPropertyValue<ScriptValue>(properties['ahoy_script']);
  const hasScript =
    typeof script?.source === 'string' && script.source.trim() !== '';

  const hasConfig = isNonEmptyObject(
    getPropertyValue<unknown>(properties['ahoy_config_schema'])
  );
  const hasState = isNonEmptyObject(
    getPropertyValue<unknown>(properties['ahoy_state_schema'])
  );

  // Boolean properties arrive as the string 'true'/'false' on the wire (every
  // existing call site compares against 'true') — compare as strings.
  const matchAll =
    String(
      getPropertyValue<unknown>(properties['ahoy_match_all_entity_types']) ??
        'false'
    ) === 'true';
  const hasTypedTriggers =
    isNonEmptyArray(
      getPropertyValue<unknown>(properties['ahoy_triggering_entity_types'])
    ) &&
    isNonEmptyArray(
      getPropertyValue<unknown>(properties['ahoy_triggering_event_types'])
    );

  let triggerKind: AutomationTriggerKind;
  if (matchAll) {
    triggerKind = 'matchAll';
  } else if (hasTypedTriggers) {
    triggerKind = 'typed';
  } else {
    triggerKind = 'unknown';
  }

  return {
    isBuiltIn,
    isManaged,
    hasScript,
    hasFixtures: hasFixtures(object),
    hasConfig,
    hasState,
    triggerKind,
  };
}
